import { EventEmitter, Injectable } from "@angular/core";
import { EmergencyContact } from "@app/project/model/emergency-contact";
import { RootStoreState } from "@app/root-store";
import { LoadingState } from "@app/root-store/api-result.state";
import { Store, select } from "@ngrx/store";
import { Observable } from "rxjs";
import { filter, switchMap } from "rxjs/operators";
import { EmergencyContactActions } from "../actions";
import { EmergencyContactSelectors } from "../selectors";

@Injectable({
    providedIn: "root",
})
export class EmergencyContactDispatchers {
    dispatch(action) {
        return this.store.dispatch(action);
    }

    clearEmergencyContacts() {
        return this.store.dispatch(EmergencyContactActions.clear());
    }

    getEmergencyContacts(projectId: number): Observable<EmergencyContact[]> {
        return this.store.pipe(
            select(EmergencyContactSelectors.getActiveEmergencyContactsState),
            filter(emergencyContactState => {
                if (emergencyContactState.callState === LoadingState.init) {
                    this.store.dispatch(EmergencyContactActions.load({ projectId }));
                }
                return emergencyContactState.callState === LoadingState.loaded;
            }),
            switchMap(() => this.store.pipe(select(EmergencyContactSelectors.getActiveEmergencyContacts)))
        );
    }

    /**
     * save contact to project
     *
     * @param contacts
     * @param emergencyContact
     * @param callbackEmitter
     */
    upsertEmergencyContact(emergencyContact: EmergencyContact, callbackEmitter: EventEmitter<any>) {
        if (!emergencyContact.id) {
            this.store.dispatch(EmergencyContactActions.create({ emergencyContact, callbackEmitter }));
        } else {
            this.store.dispatch(EmergencyContactActions.update({ emergencyContact, callbackEmitter }));
        }
    }

    /**
     * update the list of emergency contact
     *
     * @param contacts Array<EmergencyContact> list of project's emergency contact
     * @param emergencyContacts
     */
    updateEmergencyContacts(emergencyContacts: EmergencyContact[]) {
        this.store.dispatch(EmergencyContactActions.bulkUpdate({ emergencyContacts }));
    }

    /**
     * Display a user prompt before deleting a project
     *
     * @param project The project to delete
     * @param emergencyContact
     * @description Open a Mat Dialog to prompt the user before proceeding to delete a project
     */
    deleteContactConfirmation(emergencyContact: EmergencyContact) {
        this.store.dispatch(EmergencyContactActions.deleteConfirmation({ emergencyContact }));
    }

    deleteAllEmergencyContactsNoConfirmation(emergencyContacts: EmergencyContact[], callbackEmitter?: EventEmitter<any>) {
        this.store.dispatch(EmergencyContactActions.deleteAll({ emergencyContacts, callbackEmitter }));
    }

    constructor(private readonly store: Store<RootStoreState.State>) {}
}
